import { takeLatest, put, call, select } from 'redux-saga/effects';
import { initialize, arrayRemoveAll, arrayPush } from 'redux-form';

import * as act from '../../actions';
import * as modifierHubConstant from '../../actions/businessLibrary/modifier/constants';
import * as actModifierHub from '../../actions/businessLibrary/modifier';
import * as actNotif from '../../actions/notifications';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import * as apiModifierHub from '../../api/businessLibrary/modifier';
import * as apiItemsHub from '../../api/businessLibrary/items';
import { assertErrorHasMessage } from '../../utils/assert';

const geModifierHubState = (state) => state.businessLibrary.modifierHub;

function* fillApplySetItemFormFlow(modifier) {
  const form = {
    id: modifier.id,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    business_level_item_ids: modifier.business_level_item_ids,
  };
  yield put(initialize('applySetItem', form));
}

function* fillModifierFormFlow(modifier) {
  const form = {
    id: modifier.id,
    name: modifier.name,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    modifier_options: modifier.modifier_options.map((m) => {
      const { id, name } = m;
      let { price } = m;
      price = price || price === 0 ? price.toString() : price;
      return { id, name, price };
    }),
  };
  yield put(initialize('modifierHub', form));
}

function* emptyModifierFormFlow() {
  const form = {
    id: null,
    name: null,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    modifier_options: [],
  };
  yield put(initialize('modifierHub', form));
}

export function* pageRequest() {
  yield put(showPageLoadingSpinner(true));
  yield put(act.showProgressBar());
  yield call(emptyModifierFormFlow);
  yield put(actModifierHub.setModifiers([]));
  yield put(actModifierHub.setModifierDetail(null));
  yield put(actModifierHub.closeRightColumn());
  try {
    const resModifier = yield call(apiModifierHub.getModifiersList, {
      page: 0,
      size: 50,
    });
    const modifiers = resModifier.data.results;
    yield put(actModifierHub.setModifiers(modifiers));
    yield put(
      actModifierHub.setMetaModifier({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        total_page: resModifier.data.total_pages - 1,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        total_counts: resModifier.data.total_count,
        currentPage: 0,
      })
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
  yield put(act.hideProgressBar());
  yield put(showPageLoadingSpinner(false));
}

export function* clickCreateModifier() {
  yield put(actModifierHub.setModifierDetail(null));
  yield put(actModifierHub.openRightColumn());
  yield call(emptyModifierFormFlow);
}

export function* onClickApplySetItem(action) {
  const {
    payload: { modifier },
  } = action;
  try {
    yield put(act.showProgressBar());
    const resItems = yield call(apiItemsHub.getItemsHub, {
      page: 0,
      size: 50,
    });
    const { data } = resItems;
    modifier.active_item_modifiers = [];
    yield call(fillApplySetItemFormFlow, modifier);
    if (data.results) {
      yield put(actModifierHub.applySetItemSetItems(data.results));
      yield put(
        actModifierHub.applySetItemSetMeta({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_page: data.total_pages - 1,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_count: data.total_count,
          currentPage: 0,
        })
      );
    } else {
      yield put(
        actModifierHub.applySetItemSetMeta({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_page: data.total_pages - 1,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_count: data.total_count,
          currentPage: 0,
        })
      );
      yield put(actModifierHub.applySetItemSetItems([]));
    }
    yield put(actModifierHub.showApplySetItem());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onClickApplyAllItems(request) {
  const {
    payload: { modifierId },
  } = request;
  yield put(actModifierHub.setModifierApplyAllItems(modifierId));
  yield put(actModifierHub.hideApplySetItemModal());
  yield put(actModifierHub.openSetAllItemConfirmation());
}

export function* confirmApplySetItem(request) {
  const {
    payload: { data, modifierId },
  } = request;
  try {
    yield put(act.showProgressBar());
    yield put(actModifierHub.hideApplySetItemModal());
    if (data.selectAllItems) {
      yield put(
        actModifierHub.setEventAsyncJob(
          modifierHubConstant.LIBRARY_MODIFIER_HUB_APPLY_TO_ALL_CHANNEL
        )
      );
      yield call(apiModifierHub.postApplyToAllItem, modifierId);
    } else {
      yield put(
        actModifierHub.setEventAsyncJob(
          modifierHubConstant.LIBRARY_MODIFIER_HUB_APPLY_CHANNEL
        )
      );
      yield call(apiModifierHub.postApplyToItems, modifierId, data);
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  }
}

export function* onClickCancelForm() {
  yield put(actModifierHub.setModifierDetail(null));
  yield call(emptyModifierFormFlow);
  yield put(actModifierHub.closeRightColumn());
}

export function* onClickManageModifierOptions(request) {
  const {
    payload: { previousModifierForm },
  } = request;
  yield put(actModifierHub.showModifierOptionsModal());
  yield put(actModifierHub.setPreviousModifierForm(previousModifierForm));
}

export function* onCancelManageModifierOptions(request) {
  const {
    payload: { previousModifierForm },
  } = request;
  yield put(arrayRemoveAll('modifierHub', 'modifier_options'));
  for (let idx = 0; idx < previousModifierForm.modifier_options.length; idx++) {
    yield put(
      arrayPush(
        'modifierHub',
        'modifier_options',
        previousModifierForm.modifier_options[idx]
      )
    );
  }
  yield put(actModifierHub.hideModifierOptionsModal());
}

export function* onConfirmManageModifierOptions(request) {
  const { options } = request.payload;
  if (options.length === 0) {
    yield put(actNotif.error('Please save at least one option.'));
  }
  yield put(actModifierHub.hideModifierOptionsModal());
}

export function* submitFormCreate(request) {
  const {
    payload: { data },
  } = request;

  if (data.modifier.modifier_options.length === 0) {
    yield put(actNotif.error('Please save at least one option'));
  } else {
    const dataPayload = data;
    dataPayload.modifier.modifier_options = data.modifier.modifier_options.map(
      (opt) => {
        if (opt.price) {
          return opt;
        }
        return { ...opt, price: null };
      }
    );
    yield put(act.showProgressBar());
    yield put(actModifierHub.isSubmittingForm(true));
    try {
      yield put(
        actModifierHub.setEventAsyncJob(
          modifierHubConstant.LIBRARY_MODIFIER_HUB_CREATE_CHANNEL
        )
      );
      yield call(apiModifierHub.postModifierHub, dataPayload.modifier);
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(actNotif.error(error.message));
      yield put(actModifierHub.isSubmittingForm(false));
    }
  }
}

export function* onClickModifierRow(request) {
  const {
    payload: { modifier },
  } = request;
  yield put(act.showProgressBar());
  try {
    const res = yield call(apiModifierHub.getModifierDetail, modifier.id);
    yield call(fillModifierFormFlow, res.data);
    yield put(actModifierHub.setModifierDetail(res.data));
    yield put(actModifierHub.openRightColumn());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* requestModifier(action) {
  const {
    payload: { params, previousModifiers },
  } = action;
  yield put(act.showProgressBar());

  try {
    params.size = 50;
    const response = yield call(apiModifierHub.getModifiersList, params);
    const modifiers = previousModifiers.concat(response.data.results);
    yield put(actModifierHub.setModifiers(modifiers));
    yield put(
      actModifierHub.setMetaModifier({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        total_page: response.data.total_pages - 1,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        total_counts: response.data.total_count,
        currentPage: params.page,
      })
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* deleteModifier(request) {
  const {
    payload: { modifierId },
  } = request;
  yield put(act.showProgressBar());
  yield put(actModifierHub.isSubmittingForm(true));
  try {
    yield put(
      actModifierHub.setEventAsyncJob(
        modifierHubConstant.LIBRARY_MODIFIER_HUB_DELETE_CHANNEL
      )
    );
    yield call(apiModifierHub.deleteModifierHub, modifierId);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    yield put(actModifierHub.hideDeleteModifierModal());
    yield put(actModifierHub.isSubmittingForm(false));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* submitFormUpdate(request) {
  const {
    payload: { data },
  } = request;
  if (data.modifier.modifier_options.length === 0) {
    yield put(actNotif.error('Please save at least one option'));
  } else {
    yield put(act.showProgressBar());
    yield put(actModifierHub.isSubmittingForm(true));
    try {
      yield put(
        actModifierHub.setEventAsyncJob(
          modifierHubConstant.LIBRARY_MODIFIER_HUB_UPDATE_CHANNEL
        )
      );
      yield call(apiModifierHub.putItemById, data.modifier.id, data.modifier);
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(actNotif.error(error.message));
      yield put(actModifierHub.isSubmittingForm(false));
    }
  }
}

export function* handleSuccessAsyncJob(request) {
  const {
    payload: { resourceId },
  } = request;
  const { modifiers, modifierDetail, eventAsyncJob } = yield select(
    geModifierHubState
  );
  let newModifiers;
  try {
    if (
      eventAsyncJob &&
      eventAsyncJob.channelName ===
        modifierHubConstant.LIBRARY_MODIFIER_HUB_CREATE_CHANNEL
    ) {
      const respModifierDetail = yield call(
        apiModifierHub.getModifierDetail,
        resourceId
      );
      newModifiers = [...modifiers];
      newModifiers.unshift(respModifierDetail.data);

      yield put(actModifierHub.setModifierDetail(respModifierDetail.data));
      yield call(fillModifierFormFlow, {
        id: respModifierDetail.data.id,
        name: respModifierDetail.data.name,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        modifier_options: respModifierDetail.data.modifier_options,
      });
      yield put(actNotif.success('Modifier successfully created.'));
    } else if (
      eventAsyncJob &&
      eventAsyncJob.channelName ===
        modifierHubConstant.LIBRARY_MODIFIER_HUB_UPDATE_CHANNEL
    ) {
      const res = yield call(
        apiModifierHub.getModifierDetail,
        modifierDetail.id
      );
      newModifiers = modifiers.map((modifier) => {
        if (modifier.id === res.data.id) {
          return res.data;
        }
        return modifier;
      });
      yield put(actModifierHub.setModifierDetail(res.data));
      yield call(fillModifierFormFlow, {
        id: res.data.id,
        name: res.data.name,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        modifier_options: res.data.modifier_options,
      });
      yield put(actNotif.success('Modifier successfully updated.'));
    } else if (
      eventAsyncJob &&
      eventAsyncJob.channelName ===
        modifierHubConstant.LIBRARY_MODIFIER_HUB_DELETE_CHANNEL
    ) {
      newModifiers = modifiers.filter(
        (modifier) => modifier.id !== modifierDetail.id
      );
      yield put(actModifierHub.setModifierDetail(null));
      yield put(actModifierHub.setDeletedModifier(null));
      yield put(actModifierHub.hideDeleteModifierModal());
      yield put(actModifierHub.closeRightColumn());
      yield call(fillModifierFormFlow, {
        id: null,
        amount: null,
        quantity: null,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        modifier_options: [],
      });
      yield put(actNotif.success('Modifier successfully deleted.'));
    } else if (
      (eventAsyncJob &&
        eventAsyncJob.channelName ===
          modifierHubConstant.LIBRARY_MODIFIER_HUB_APPLY_CHANNEL) ||
      eventAsyncJob.channelName ===
        modifierHubConstant.LIBRARY_MODIFIER_HUB_APPLY_CHANNEL
    ) {
      yield put(
        actModifierHub.modifiersRequest(
          {
            page: 0,
          },
          []
        )
      );
      yield put(actModifierHub.setModifierDetail(null));
      yield put(actModifierHub.closeRightColumn());
      yield put(actNotif.success('Successfully applied modifier'));
    }
    yield put(actModifierHub.setModifiers(newModifiers));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(actModifierHub.isSubmittingForm(false));
    yield put(actModifierHub.setEventAsyncJob(null));
    yield put(act.hideProgressBar());
    yield put(actModifierHub.closeSetAllItemConfirmation());
  }
}

export function* handleFailedAsyncJob(request) {
  const {
    payload: { error },
  } = request;
  yield put(actNotif.error(error || 'Failed process modifiers'));
  yield put(actModifierHub.isSubmittingForm(false));
  yield put(actModifierHub.setEventAsyncJob(null));
}

export function* applySetItemItemsRequest(request) {
  const {
    payload: { params, previousItems },
  } = request;
  try {
    yield put(act.showProgressBar());
    let results = [];
    const resItems = yield call(apiItemsHub.getItemsHub, params);
    const { data } = resItems;

    if (params && params.keyword) {
      results = data.results;
    } else {
      results = previousItems.concat(data.results);
    }
    if (results) {
      yield put(actModifierHub.applySetItemSetItems(results));
      yield put(
        actModifierHub.applySetItemSetMeta({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_page: data.total_pages - 1,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_count: data.total_count,
          currentPage: params.page,
        })
      );
    } else {
      yield put(
        actModifierHub.applySetItemSetMeta({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_page: data.total_pages - 1,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          total_count: data.total_count,
          currentPage: params.page,
        })
      );
      yield put(actModifierHub.applySetItemSetItems([]));
    }
    yield put(actModifierHub.showApplySetItem());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export default function* saga() {
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_PAGE_REQUEST,
    pageRequest
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_ON_CLICK_CREATE_MODIFIER,
    clickCreateModifier
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_ON_CLICK_APPLY_SET_ITEM,
    onClickApplySetItem
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_ON_CLICK_APPLY_ALL_ITEMS,
    onClickApplyAllItems
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_CONFIRM_APPLY_SET_ITEM,
    confirmApplySetItem
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_ON_CLICK_CANCEL,
    onClickCancelForm
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_ON_CLICK_MANAGE_MODIFIER_OPTIONS,
    onClickManageModifierOptions
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_ON_CANCEL_MANAGE_MODIFIER_OPTIONS,
    onCancelManageModifierOptions
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_ON_CONFIRM_MANAGE_MODIFIER_OPTIONS,
    onConfirmManageModifierOptions
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_SUBMIT_FORM_CREATE,
    submitFormCreate
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_ON_CLICK_ROW,
    onClickModifierRow
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_REQUEST_MODIFIERS,
    requestModifier
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_DELETE_MODIFIER,
    deleteModifier
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_SUBMIT_FORM_UPDATE,
    submitFormUpdate
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_HANDLE_SUCCESS_ASYNC_JOB,
    handleSuccessAsyncJob
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_APPLY_SET_ITEM_ITEMS_REQUEST,
    applySetItemItemsRequest
  );
  yield takeLatest(
    modifierHubConstant.LIBRARY_MODIFIER_HUB_HANDLE_FAILED_ASYNC_JOB,
    handleFailedAsyncJob
  );
}
